// src/pages/locations/regions/CreateRegionPage.tsx
import React from "react";
import { useNavigate } from "react-router-dom";

import { useCountries, useCreateRegionMutation } from "@/api/features/locations";
import RegionForm from "@/components/locations/forms/RegionForm";

import LocationFormPage from "../components/LocationFormPage";
import { useLeaveConfirm } from "../components/form/useLeaveConfirm";
import { useLocationScope } from "../useLocationScope";
import { locationTabPath } from "../locations.access";
import { withFlash } from "../flash";

const CreateRegionPage: React.FC = () => {
  const navigate = useNavigate();
  const { country, scope } = useLocationScope();

  const backTo = locationTabPath("regions", scope);

  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: 100,
  });
  const countries = countriesRes?.items ?? [];

  const createMutation = useCreateRegionMutation();
  const { requestCancel, dialog } = useLeaveConfirm(() => navigate(backTo));

  const [resetSignal, setResetSignal] = React.useState(0);

  return (
    <LocationFormPage
      entity="regions"
      title="Add region"
      backTo={backTo}
      backLabel="Back to Regions"
    >
      <RegionForm
        mode="create"
        countries={countries}
        countriesLoading={countriesLoading}
        defaultCountryIso2={country}
        isSubmitting={createMutation.isPending}
        error={createMutation.error}
        resetSignal={resetSignal}
        onCancel={requestCancel}
        onSubmit={(values, { addAnother }) =>
          createMutation.mutate(
            {
              countryIso2: values.countryIso2,
              name: values.name,
              code: values.code || null,
            },
            {
              onSuccess: (created) => {
                if (addAnother) {
                  setResetSignal((n) => n + 1);
                  return;
                }
                // Land on the country the region was actually filed under.
                navigate(
                  locationTabPath("regions", {
                    country: created?.countryIso2 ?? values.countryIso2,
                  }),
                  withFlash(`${created.name} was added.`)
                );
              },
            }
          )
        }
      />

      {dialog}
    </LocationFormPage>
  );
};

export default CreateRegionPage;
